import { District } from '../districts/District';

export type ContestShapefileStatus =
  | 'processing'
  | 'labeling'
  | 'needs-review'
  | 'creating'
  | 'completed'
  | 'error'
  | 'canceled';

export interface ContestShapefileStatusEntry {
  status: ContestShapefileStatus;
  enteredAt: string | Date;
  details?: Record<string, unknown>;
}

export interface ContestShapefileRowMatch {
  districtId?: string | null;
  district?: District | null;
  confidence?: number | null;
  createWithData?: {
    number?: number;
    name?: string;
    longName?: string;
    letter?: string;
    type?: string;
    parentId?: string;
    parentDistrict?: string | District;
  } | null;
  inputText?: string | null;
}

export interface ContestShapefileRow {
  dataIndex: number;
  contest: string;
  geometry?: any;
  match: ContestShapefileRowMatch | null;
  boundaryComparison?: {
    areaA: number;
    areaB: number;
    areaDifferenceRatio: number;
    boundaryDifferenceRatio: number;
    differenceArea: number;
    unionArea?: number;
    significantDifference: boolean;
  } | null;
  parentBoundaryComparison?: {
    parentDistrictId: string;
    incomingArea: number;
    containedArea: number;
    containedRatio: number;
    minimumContainedRatio: number;
    insufficientContainment: boolean;
    manuallyApproved?: boolean;
    manuallyApprovedAt?: string | Date;
    manuallyApprovedBy?: string;
  } | null;
  boundaryAction?: 'append' | null;
  included: boolean;
  reviewed: boolean;
  creationStatus?: string | null;
  creationError?: { message?: string } | null;
}

export interface ContestShapefile {
  _id: string;
  election: string;
  county: string;
  countySearchTerm?: string;
  key: string;
  bucket: string;
  location?: string;
  status: ContestShapefileStatus;
  totalRows: number;
  processedRows: number;
  matchedRows: number;
  creationTotal: number;
  creationProcessed: number;
  creationSucceeded: number;
  creationFailed: number;
  dataOperationsError?: string | null;
  expectedContestsByDistrict?: Record<string, string[]>;
  statusEntries: ContestShapefileStatusEntry[];
  processInProgress?: {
    stage?: 'none' | 'processing' | 'labeling' | 'creating';
    runId?: string;
    startedAt?: string | Date;
  };
  error?: string | { message?: string } | null;
  rows?: ContestShapefileRow[];
  updatedRow?: ContestShapefileRow;
  boundaryComparisonBackfill?: {
    totalRows: number;
    eligibleRows: number;
    comparedRows: number;
    parentComparedRows?: number;
    parentWarningRows?: number;
    appendedRows?: number;
    unavailableRows: number;
  };
  parentBoundaryComparisonRecalculation?: {
    totalRows: number;
    matchedRows: number;
    comparedRows: number;
    warningRows: number;
    unavailableRows: number;
  };
  rowTotal?: number;
  creationStartedAt?: string | Date | null;
  createdAt?: string | Date;
  updatedAt?: string | Date;
}

export const getContestShapefileErrorMessage = (record: ContestShapefile): string | null => {
  const recordError = record.error;
  if (typeof recordError === 'string') {
    if (recordError) return recordError;
  } else if (recordError?.message) {
    return recordError.message;
  }

  const latestErrorEntry = [...(record.statusEntries || [])]
    .reverse()
    .find(entry => entry.status === 'error');
  const statusEntryMessage = latestErrorEntry?.details?.message;
  if (typeof statusEntryMessage === 'string' && statusEntryMessage) return statusEntryMessage;

  if (record.dataOperationsError) return record.dataOperationsError;
  return record.status === 'error' ? 'Processing failed.' : null;
};

export interface CreateContestShapefile {
  election: string;
  county: string;
  key: string;
  bucket: string;
  location?: string;
}

export interface PatchContestShapefile {
  $updateRowMatch?: {
    dataIndex: number;
    districtId: string | null;
  };
  $setRowIncluded?: {
    dataIndex: number;
    included: boolean;
  };
  $approveParentBoundaryComparison?: {
    dataIndex: number;
  };
  $retry?: true | {
    stage: 'processing' | 'labeling' | 'creating';
    triggerProcessing?: boolean;
  };
  $cancel?: true;
  $backfillBoundaryComparisons?: true;
  $recalculateParentBoundaryComparisons?: true;
  $regenerateDataOperations?: true;
  $createGeoDistricts?: true;
}
